import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { noticesService } from '../services/noticesService';
import { sanitizeNoticeParagraphs } from '../utils/noticeMarkdown';
import { useWebSocket } from '../contexts/WebSocketContext';
import type { Notice, NoticeType } from '../types/notices';

const ANNOUNCEMENTS_CHANNEL = 'announcements.public';

// Simple markdown renderer that delegates sanitization to the hardened util
const renderMarkdown = (text: string): React.ReactNode =>
  sanitizeNoticeParagraphs(text).map((html, pIndex) => (
    <p
      key={pIndex}
      className="mb-2 last:mb-0"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ));

// Notice type styling configuration
const noticeTypeConfig: Record<NoticeType, { color: string; bgColor: string; icon: React.ReactNode }> = {
  info: {
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
  },
  warning: {
    color: 'text-accent-yellow',
    bgColor: 'bg-accent-yellow/10',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
  },
  urgent: {
    color: 'text-bear-red',
    bgColor: 'bg-red-500/10',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
  },
  maintenance: {
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
      </svg>
    ),
  },
  reload: {
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
      </svg>
    ),
  },
};

// Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const NoticesButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNoticeId, setExpandedNoticeId] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  // Fetch notices
  const { data: notices = [], isLoading } = useQuery({
    queryKey: ['notices'],
    queryFn: () => noticesService.getNotices(),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });

  // Calculate unread count locally
  const unreadCount = notices.filter((n) => !n.is_read).length;

  // Mark single notice as read
  const markReadMutation = useMutation({
    mutationFn: (noticeId: number) => noticesService.markAsRead(noticeId),
    onSuccess: (_, noticeId) => {
      // Optimistically update the cache
      queryClient.setQueryData<Notice[]>(['notices'], (old) =>
        old?.map((n) => (n.id === noticeId ? { ...n, is_read: true } : n))
      );
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: () => noticesService.markAllAsRead(),
    onSuccess: () => {
      // Optimistically update the cache
      queryClient.setQueryData<Notice[]>(['notices'], (old) =>
        old?.map((n) => ({ ...n, is_read: true }))
      );
    },
  });

  // Handle clicking a notice
  const handleNoticeClick = useCallback((notice: Notice) => {
    if (expandedNoticeId === notice.id) {
      setExpandedNoticeId(null);
    } else {
      setExpandedNoticeId(notice.id);
      if (!notice.is_read) {
        markReadMutation.mutate(notice.id);
      }
    }
  }, [expandedNoticeId, markReadMutation]);

  // Handle mark all as read
  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setExpandedNoticeId(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    return subscribe(ANNOUNCEMENTS_CHANNEL, (payload) => {
      const { type, notice } = (payload as { data?: { type?: string; notice?: Notice } }).data || {};

      if (type === 'notice.new' && notice) {
        // Add new notice to the top of the list
        queryClient.setQueryData<Notice[]>(['notices'], (old) => {
          if (!old) return [{ ...notice, is_read: false }];
          // Check if notice already exists
          if (old.some((n) => n.id === notice.id)) return old;
          return [{ ...notice, is_read: false }, ...old];
        });
      } else if (type === 'notice.update' && notice) {
        // Update existing notice
        queryClient.setQueryData<Notice[]>(['notices'], (old) =>
          old?.map((n) => (n.id === notice.id ? { ...n, ...notice } : n))
        );
      }
    });
  }, [subscribe, queryClient]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-200 transition-colors"
        title="Notices"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-bear-red rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-dark-border bg-dark-surface-light">
            <h3 className="text-sm font-semibold text-gray-100">Notices</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-gray-400 hover:text-bull-blue transition-colors"
                disabled={markAllReadMutation.isPending}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notices List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-bull-blue"></div>
              </div>
            ) : notices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <p className="text-sm">No notices</p>
              </div>
            ) : (
              notices.map((notice) => {
                const typeConfig = noticeTypeConfig[notice.notice_type] ?? noticeTypeConfig.info;
                const isExpanded = expandedNoticeId === notice.id;

                return (
                  <div
                    key={notice.id}
                    className={`border-b border-dark-border last:border-b-0 transition-colors ${
                      !notice.is_read ? 'bg-dark-bg/50' : ''
                    }`}
                  >
                    {/* Notice Header (clickable) */}
                    <button
                      onClick={() => handleNoticeClick(notice)}
                      className="w-full text-left px-4 py-3 hover:bg-dark-surface-light transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Type Icon */}
                        <div className={`flex-shrink-0 p-1 rounded ${typeConfig.bgColor} ${typeConfig.color}`}>
                          {typeConfig.icon}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={`text-sm font-medium ${!notice.is_read ? 'text-gray-100' : 'text-gray-300'}`}>
                              {notice.title}
                            </h4>
                            {!notice.is_read && (
                              <span className="flex-shrink-0 w-2 h-2 bg-bull-blue rounded-full"></span>
                            )}
                          </div>
                          {!isExpanded && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                              {notice.message.replace(/[*`[\]()#]/g, '').substring(0, 60)}
                              {notice.message.length > 60 ? '...' : ''}
                            </p>
                          )}
                          <p className="text-xs text-gray-600 mt-1">
                            {formatRelativeTime(notice.created_at)}
                          </p>
                        </div>

                        {/* Expand indicator */}
                        <svg
                          className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Expanded Message */}
                    {isExpanded && (
                      <div className="px-4 pb-3">
                        <div className="ml-9 text-sm text-gray-300 leading-relaxed">
                          {renderMarkdown(notice.message)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
